import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Video,
  Headphones,
  FileTextIcon,
  ChevronDown,
  ChevronUp,
  Save,
  Edit
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'react-hot-toast';

interface Interview {
  id: string;
  expert_name: string;
  language?: string;
  video_url?: string;
  audio_url?: string;
  transcript_text?: string;
}

interface ArticleGenerationModalProps {
  interview: Interview;
  open: boolean;
  onClose: () => void;
  onSuccess: (article: any) => void;
}

interface GeneratedArticle {
  id: string;
  title: string;
  content: string;
  word_count: number;
  language: string;
  style: string;
  created_at: string;
}

export function ArticleGenerationModal({
  interview,
  open,
  onClose,
  onSuccess
}: ArticleGenerationModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedArticle, setGeneratedArticle] = useState<GeneratedArticle | null>(null);

  // Transcript editing state
  const [transcriptEditorOpen, setTranscriptEditorOpen] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState(interview.transcript_text || '');
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form state
  const [language, setLanguage] = useState<string>(interview.language || 'en');
  const [style, setStyle] = useState<string>('professional');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [includeVideoReference, setIncludeVideoReference] = useState(false);
  const [includeAudioReference, setIncludeAudioReference] = useState(false);
  const [includeTranscription, setIncludeTranscription] = useState(false);

  // Fetch current user to use as reviewer
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await authFetch('/api/users/me');
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData.user);
        }
      } catch (err) {
        console.error('[ArticleGeneration] Error fetching current user:', err);
      }
    };

    if (open) {
      fetchCurrentUser();
    }
  }, [open]);

  const handleSaveTranscript = async () => {
    try {
      setSavingTranscript(true);

      const response = await authFetch(
        `/api/interviews/${interview.id}/transcript`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript: editedTranscript })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save transcript');
      }

      toast.success('Transcript saved successfully');
      setHasUnsavedChanges(false);

      // Update the interview object with the new transcript
      interview.transcript_text = editedTranscript;

    } catch (err) {
      console.error('[ArticleGeneration] Error saving transcript:', err);
      toast.error('Failed to save transcript');
    } finally {
      setSavingTranscript(false);
    }
  };

  const handleTranscriptChange = (value: string) => {
    setEditedTranscript(value);
    setHasUnsavedChanges(value !== interview.transcript_text);
  };

  const handleGenerateArticle = async () => {
    try {
      setLoading(true);
      setError(null);
      setGeneratedArticle(null);

      console.log('[ArticleGeneration] Generating article for interview:', interview.id);

      const response = await authFetch(
        `/api/interviews/${interview.id}/articles/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            language,
            style,
            reviewerName: currentUser?.name,
            reviewerTitle: currentUser?.title,
            reviewerCompany: currentUser?.company,
            includeVideoReference,
            includeAudioReference,
            includeTranscription,
            videoUrl: interview.video_url,
            audioUrl: interview.audio_url
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate article');
      }

      const data = await response.json();
      console.log('[ArticleGeneration] Article generated successfully:', data.article);

      setGeneratedArticle(data.article);
      onSuccess(data.article);

    } catch (err) {
      console.error('[ArticleGeneration] Error generating article:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate article');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setGeneratedArticle(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Article from Interview
          </DialogTitle>
          <DialogDescription>
            Create an HTML article from the interview transcription with {interview.expert_name}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {generatedArticle && (
          <Alert className="border-green-500 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <div className="font-semibold mb-1">{generatedArticle.title}</div>
              <div className="text-sm">
                {generatedArticle.word_count} words • {generatedArticle.language.toUpperCase()} • {generatedArticle.style}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {!generatedArticle && interview.transcript_text && (
          <Collapsible
            open={transcriptEditorOpen}
            onOpenChange={setTranscriptEditorOpen}
            className="border rounded-lg"
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  <span className="font-medium">Review & Edit Transcript</span>
                  {hasUnsavedChanges && (
                    <span className="text-xs text-orange-600 font-semibold">(Unsaved changes)</span>
                  )}
                </div>
                {transcriptEditorOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Review and correct any transcription errors before generating the article.
                Changes will be saved to the interview record.
              </p>
              <Textarea
                value={editedTranscript}
                onChange={(e) => handleTranscriptChange(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
                placeholder="Transcript text..."
                disabled={savingTranscript || loading}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  {editedTranscript.length} characters
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditedTranscript(interview.transcript_text || '');
                      setHasUnsavedChanges(false);
                    }}
                    disabled={!hasUnsavedChanges || savingTranscript || loading}
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveTranscript}
                    disabled={!hasUnsavedChanges || savingTranscript || loading}
                  >
                    {savingTranscript ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Transcript
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {!generatedArticle && (
          <div className="space-y-6">
            {/* Language Selection */}
            <div className="space-y-2">
              <Label htmlFor="language">Article Language</Label>
              <Select value={language} onValueChange={setLanguage} disabled={loading}>
                <SelectTrigger id="language">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish (Español)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Can be different from the original transcription language
              </p>
            </div>

            {/* Style Selection */}
            <div className="space-y-2">
              <Label htmlFor="style">Writing Style</Label>
              <Select value={style} onValueChange={setStyle} disabled={loading}>
                <SelectTrigger id="style">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">
                    Professional - Authoritative, well-researched
                  </SelectItem>
                  <SelectItem value="conversational">
                    Conversational - Friendly, accessible
                  </SelectItem>
                  <SelectItem value="technical">
                    Technical - Precise, detailed for experts
                  </SelectItem>
                  <SelectItem value="marketing">
                    Marketing - Persuasive, benefit-focused
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Media References */}
            <div className="space-y-3">
              <Label>Include References</Label>

              {interview.video_url && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="video-ref"
                    checked={includeVideoReference}
                    onCheckedChange={(checked) => setIncludeVideoReference(checked === true)}
                    disabled={loading}
                  />
                  <label
                    htmlFor="video-ref"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                  >
                    <Video className="h-4 w-4" />
                    Include video reference/embed
                  </label>
                </div>
              )}

              {interview.audio_url && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="audio-ref"
                    checked={includeAudioReference}
                    onCheckedChange={(checked) => setIncludeAudioReference(checked === true)}
                    disabled={loading}
                  />
                  <label
                    htmlFor="audio-ref"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                  >
                    <Headphones className="h-4 w-4" />
                    Include audio reference
                  </label>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="transcription"
                  checked={includeTranscription}
                  onCheckedChange={(checked) => setIncludeTranscription(checked === true)}
                  disabled={loading}
                />
                <label
                  htmlFor="transcription"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                >
                  <FileTextIcon className="h-4 w-4" />
                  Include literal transcription in article
                </label>
              </div>
            </div>

            {/* Info Alert */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                The article will be generated using AI (GPT-4) based on the interview transcription.
                The process may take 20-30 seconds. Generated articles are saved and linked to this interview.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {generatedArticle ? 'Close' : 'Cancel'}
          </Button>

          {!generatedArticle && (
            <Button
              onClick={handleGenerateArticle}
              disabled={loading || !interview.transcript_text}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Article...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Article
                </>
              )}
            </Button>
          )}
        </div>

        {!interview.transcript_text && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This interview needs a transcription before generating articles.
              Please complete the interview recording and transcription first.
            </AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
